import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { BookOpen, Heart, Loader2, Trash2 } from "lucide-react";

import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";

import { useRequest } from "@/hooks/use-request";
import { cn } from "@/lib/utils";
import { requireAuth } from "@/middleware/auth";
import { useBookmarkQuery } from "@/queries/bookmark";

export const Route = createFileRoute("/bookmark/")({
	beforeLoad: async () => {
		await requireAuth();
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { del } = useRequest();
	const queryClient = useQueryClient();
	const queryOptions = useBookmarkQuery();
	const { data, isLoading, error } = useQuery(queryOptions);

	const { mutate, isPending } = useMutation({
		mutationFn: async (pack_id: string) => {
			const result = confirm("북마크에서 제거하시겠습니까?");

			if (result) {
				return await del("bookmark", {
					pack_id,
				});
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["bookmark"] });
		},
	});

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-full bg-gray-50">
				<div className="flex flex-col items-center gap-4">
					<Loader2 className="size-12 animate-spin text-blue-500" />
					<p className="text-lg text-gray-600 font-medium">
						북마크를 불러오는 중...
					</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center items-center h-full bg-gray-50">
				<div className="flex flex-col items-center gap-4">
					<div className="size-16 bg-red-100 rounded-full flex items-center justify-center">
						<Heart className="size-8 text-red-500" />
					</div>
					<p className="text-lg text-gray-600 font-medium">
						북마크를 불러올 수 없습니다.
					</p>
					<Button onClick={() => window.location.reload()}>다시 시도</Button>
				</div>
			</div>
		);
	}

	return (
		<>
			<Header />
			<div className="min-h-screen bg-white">
				{/* 메인 콘텐츠 */}
				<div className="max-w-7xl mx-auto px-6 py-12">
					{data && data.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{data.map((bookmark) => (
								<div
									key={bookmark.pack_id}
									className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 group"
								>
									{/* 썸네일 영역 */}
									<div className="relative aspect-video bg-white overflow-hidden">
										{bookmark.pack_thumbnail_url ? (
											<img
												src={bookmark.pack_thumbnail_url}
												alt={bookmark.pack_name}
												className="w-full h-full object-contain bg-white"
												onError={(e) => {
													const target = e.target as HTMLImageElement;
													target.style.display = "none";
													target.nextElementSibling?.classList.remove("hidden");
												}}
											/>
										) : null}

										{/* 썸네일이 없을 때의 기본 아이콘 */}
										<div
											className={cn(
												"absolute inset-0 flex items-center justify-center bg-gradient-to-br from-red-50 to-pink-50",
												bookmark.pack_thumbnail_url ? "hidden" : "",
											)}
										>
											<div className="flex flex-col items-center gap-3">
												<div className="size-16 bg-white rounded-2xl flex items-center justify-center shadow-sm">
													<Heart className="size-8 text-red-600" />
												</div>
												<div className="text-center">
													<div className="text-sm font-medium text-red-700 bg-red-100 px-3 py-1 rounded-full">
														북마크
													</div>
												</div>
											</div>
										</div>

										{/* 제목 오버레이 */}
										<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end">
											<div className="p-4 w-full">
												<h3 className="text-white font-bold text-lg line-clamp-2">
													{bookmark.pack_name}
												</h3>
											</div>
										</div>

										{/* 슬라이드 수 배지 */}
										<div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
											{bookmark.pack_slide_count || 0}p
										</div>

										{/* 북마크 삭제 버튼 */}
										<Button
											variant="ghost"
											size="icon"
											className="absolute top-3 left-3 bg-white/90 hover:bg-white text-red-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
											onClick={() => mutate(bookmark.pack_id)}
											disabled={isPending}
										>
											<Trash2 className="size-4" />
										</Button>
									</div>

									{/* 메타데이터 영역 */}
									<div className="p-4">
										{/* 출처 정보 */}
										{bookmark.pack_source && (
											<div className="flex items-center gap-2 mb-2">
												{bookmark.pack_source_logo_url && (
													<img
														src={bookmark.pack_source_logo_url}
														alt="출처 로고"
														className="size-5 rounded-full object-cover"
														onError={(e) => {
															const target = e.target as HTMLImageElement;
															target.style.display = "none";
														}}
													/>
												)}
												<div className="flex-1 min-w-0">
													<p className="text-sm text-gray-600 truncate">
														{bookmark.pack_source}
													</p>
												</div>
												<div className="size-4 bg-red-400 rounded-full flex items-center justify-center">
													<div className="size-2 bg-white rounded-full" />
												</div>
											</div>
										)}

										{/* 설명 */}
										{bookmark.pack_description && (
											<p className="text-gray-500 text-sm line-clamp-2 mb-4">
												{bookmark.pack_description}
											</p>
										)}

										{/* 보기 버튼 */}
										<Link
											to="/pack/$pack_id"
											params={{ pack_id: bookmark.pack_id }}
											className="block w-full"
										>
											<Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
												<BookOpen className="size-4 mr-2" />
												보기
											</Button>
										</Link>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="flex flex-col items-center justify-center py-20">
							<h3 className="text-2xl font-bold text-gray-900 mb-2">
								북마크가 없습니다
							</h3>
							<p className="text-gray-600 text-center max-w-md">
								아직 북마크한 슬라이드가 없습니다. 슬라이드를 보고 북마크를
								추가해보세요.
							</p>
							<Link to="/pack" className="mt-6">
								<Button className="bg-blue-600 hover:bg-blue-700 text-white">
									슬라이드 보기
								</Button>
							</Link>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
